import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { ReportRepository } from '../../application/ReportRepository'
import {
  DATASETS,
  ReportCommandError,
  reportErrorCode,
  type BeginReportCommand,
  type DatasetKey,
  type Period,
  type ReportJob,
} from '../../domain/report'

const datasetKeys = DATASETS.map((dataset) => dataset.key) as [DatasetKey, ...DatasetKey[]]

export const reportJobSchema = z.object({
  jobId: z.string(),
  kind: z.enum(['EXPORT', 'FINANCIAL_REPORT', 'BACKUP']),
  category: z.string(),
  parameters: z
    .object({
      datasets: z.array(z.string()).optional(),
      filters: z.record(z.string(), z.unknown()).optional(),
      periodKey: z.string().optional(),
      axis: z.string().optional(),
    })
    .loose(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  reason: z.string().nullable(),
  status: z.enum(['queued', 'processing', 'completed', 'failed', 'expired']),
  progressPercent: z.number(),
  currentStep: z.string().nullable(),
  requestedBy: z.object({ userId: z.string(), name: z.string().nullable() }),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  snapshotAt: z.string().nullable(),
  fileName: z.string().nullable(),
  sizeBytes: z.number().nullable(),
  rowCounts: z.record(z.string(), z.number()).nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  deletedAt: z.string().nullable(),
}) satisfies z.ZodType<ReportJob>

const exportPreviewSchema = z.object({
  datasets: z.array(
    z.object({
      dataset: z.enum(datasetKeys),
      family: z.enum(['CLIENTS', 'CONTRACTS', 'BILLING', 'OPERATIONS', 'ORGANIZATION']),
      allowed: z.boolean(),
      count: z.number().nullable(),
      missingPermission: z.string().nullable().optional(),
    }),
  ),
})

const financialPreviewSchema = z.object({
  report: z.enum(['SALES_SUMMARY', 'TAXES', 'PAYMENTS', 'RECEIVABLES', 'REVENUE']),
  summary: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      amount: z.coerce.number(),
      detail: z.string().optional(),
    }),
  ),
  sheet: z.string().nullable(),
  rowCount: z.number(),
  rows: z.array(z.record(z.string(), z.unknown())),
})

const filterOptionSchema = z.array(
  z.object({ value: z.string(), label: z.string(), detail: z.string().nullable().optional() }),
)
const filterOptionsSchema = z.object({
  routes: filterOptionSchema,
  sectors: filterOptionSchema,
  operators: filterOptionSchema,
  equipments: filterOptionSchema,
  operations: filterOptionSchema,
})

const downloadSchema = z.object({ url: z.string(), fileName: z.string() })

interface RpcResponse {
  data: unknown
  error: { message: string } | null
}

/** Corps d'erreur `{ error: 'CODE' }` renvoyé par la fonction Edge. */
async function functionErrorCode(error: unknown): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context
  if (context instanceof Response) {
    const body = (await context.json().catch(() => null)) as { error?: unknown } | null
    if (typeof body?.error === 'string') return body.error
  }
  return 'UNEXPECTED'
}

function periodFields(period: Period) {
  return {
    periodKey: period.key,
    ...(period.start ? { periodStart: period.start } : {}),
    ...(period.end ? { periodEnd: period.end } : {}),
  }
}

/** Commande de `begin_report_job_v1` : seulement les clés que la base accepte pour ce type. */
export function beginCommandPayload(
  command: BeginReportCommand,
  idempotencyKey: string,
): Record<string, unknown> {
  switch (command.kind) {
    case 'EXPORT':
      return {
        idempotencyKey,
        kind: 'EXPORT',
        datasets: command.datasets,
        ...(command.filters ? { filters: command.filters } : {}),
        ...periodFields(command.period),
      }
    case 'FINANCIAL_REPORT':
      return {
        idempotencyKey,
        kind: 'FINANCIAL_REPORT',
        report: command.report,
        ...(command.report === 'REVENUE' && command.axis ? { axis: command.axis } : {}),
        ...periodFields(command.period),
      }
    case 'BACKUP':
      return { idempotencyKey, kind: 'BACKUP', reason: command.reason.trim() }
  }
}

export function createSupabaseReportRepository(client: AppSupabaseClient): ReportRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function call(name: string, args?: Record<string, unknown>): Promise<unknown> {
    const response = (await rpcClient.rpc(name, args)) as unknown as RpcResponse
    if (response.error) throw new ReportCommandError(reportErrorCode(response.error.message))
    return response.data
  }

  return {
    async exportPreview({ datasets, filters, period }) {
      const data = exportPreviewSchema.parse(
        await call('report_export_preview_v1', {
          p_command: {
            ...(datasets ? { datasets } : {}),
            ...(filters ? { filters } : {}),
            ...(period.start ? { periodStart: period.start } : {}),
            ...(period.end ? { periodEnd: period.end } : {}),
          },
        }),
      )
      return data.datasets.map((row) => ({ ...row, missingPermission: row.missingPermission ?? null }))
    },
    async filterOptions(period) {
      const data = filterOptionsSchema.parse(
        await call('report_filter_options_v1', {
          p_command: {
            ...(period.start ? { periodStart: period.start } : {}),
            ...(period.end ? { periodEnd: period.end } : {}),
          },
        }),
      )
      const clean = (options: z.infer<typeof filterOptionSchema>) =>
        options.map(({ value, label, detail }) => ({ value, label, detail: detail ?? undefined }))
      return {
        routes: clean(data.routes),
        sectors: clean(data.sectors),
        operators: clean(data.operators),
        equipments: clean(data.equipments),
        operations: clean(data.operations),
      }
    },
    async financialPreview({ report, axis, period }) {
      return financialPreviewSchema.parse(
        await call('report_financial_preview_v1', {
          p_command: {
            report,
            ...(report === 'REVENUE' && axis ? { axis } : {}),
            ...(period.start ? { periodStart: period.start } : {}),
            ...(period.end ? { periodEnd: period.end } : {}),
          },
        }),
      )
    },
    async begin(command, idempotencyKey) {
      const job = reportJobSchema.parse(
        await call('begin_report_job_v1', { p_command: beginCommandPayload(command, idempotencyKey) }),
      )
      // Réveil immédiat de la génération; le cron prend le relais si cet appel se perd.
      void client.functions
        .invoke('report-exports', { body: { action: 'work', jobId: job.jobId } })
        .catch(() => undefined)
      return job
    },
    async jobs(query = {}) {
      const data = z
        .object({ jobs: z.array(reportJobSchema) })
        .parse(await call('report_jobs_v1', { p_query: query }))
      return data.jobs
    },
    async job(jobId) {
      return reportJobSchema.parse(await call('report_job_v1', { p_job_id: jobId }))
    },
    async download(jobId) {
      const invocation = await client.functions.invoke('report-exports', {
        body: { action: 'download', jobId },
      })
      if (invocation.error) throw new ReportCommandError(await functionErrorCode(invocation.error))
      return downloadSchema.parse(invocation.data)
    },
    async deleteFile(jobId) {
      return reportJobSchema.parse(await call('delete_report_job_file_v1', { p_command: { jobId } }))
    },
  }
}
